/**
 * Timetable widget
 * @module components/theme/Widgets/TimetableWidget
 */

import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { defineMessages, FormattedMessage } from 'react-intl';
import { Table } from 'semantic-ui-react';
import { getVocabulary } from '@plone/volto/actions/vocabularies/vocabularies';
import { useDispatch, useSelector } from 'react-redux';
import { default as FormattedDate } from '@plone/volto/components/theme/FormattedDate/FormattedDate';
import { default as Icon } from '@plone/volto/components/theme/Icon/Icon';
import checkSVG from '@plone/volto/icons/check.svg';

const messages = defineMessages({
  day: {
    id: 'day',
    defaultMessage: 'Day',
  },
  fromDate: {
    id: 'fromDate',
    defaultMessage: 'From Date',
  },
  fromTime: {
    id: 'fromTime',
    defaultMessage: 'From Time',
  },
  orderTo: {
    id: 'orderTo',
    defaultMessage: 'Order to',
  },
  repeatEveryYear: {
    id: 'repeatEveryYear',
    defaultMessage: 'Repeat every Year',
  },
  toDate: {
    id: 'toDate',
    defaultMessage: 'To Date',
  },
  toTime: {
    id: 'toTime',
    defaultMessage: 'To Time',
  },
});

const DAYS_VOCABULARY = 'apanymantel.cart.shopping.days';
/**
 * Component to display a timetable.
 * @function Field
 * @param {Array} timetable Timetable.
 * @returns {string} Markup of the component.
 */
const TimetableClassicWidget = ({ value }) => {
  const dispatch = useDispatch();

  const hasOrderTo = value.some((obj) => obj.hasOwnProperty('orderTo'));
  useEffect(() => {
    dispatch(getVocabulary({ vocabNameOrURL: DAYS_VOCABULARY }));
  }, [dispatch]);

  const daysVocab = useSelector((state) => state.vocabularies[DAYS_VOCABULARY]);

  const getDay = (day) => {
    const dayTerms = daysVocab?.items?.filter(
      (dayTerm) => dayTerm.value === day,
    );

    if (dayTerms && dayTerms.length > 0) {
      return dayTerms[0].label;
    }
  };

  return (
    <Table celled>
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell>
            <FormattedMessage id="fromDate" defaultMessage="From Date" />
          </Table.HeaderCell>
          <Table.HeaderCell>
            <FormattedMessage id="toDate" defaultMessage="To Date" />
          </Table.HeaderCell>
          <Table.HeaderCell>
            <FormattedMessage
              id="repeatEveryYear"
              defaultMessage="Repeat every Year"
            />
          </Table.HeaderCell>
          <Table.HeaderCell>
            <FormattedMessage id="day" defaultMessage="Day" />
          </Table.HeaderCell>
          <Table.HeaderCell>
            <FormattedMessage id="fromTime" defaultMessage="From Time" />
          </Table.HeaderCell>
          <Table.HeaderCell>
            <FormattedMessage id="toTime" defaultMessage="To Time" />
          </Table.HeaderCell>
          {hasOrderTo && (
            <Table.HeaderCell>
              <FormattedMessage id="orderTo" defaultMessage="Order to" />
            </Table.HeaderCell>
          )}
        </Table.Row>
      </Table.Header>

      <Table.Body>
        {value.map((row, idx) => (
          <Table.Row key={idx}>
            <Table.Cell>
              {row.from_date && <FormattedDate date={row.from_date} />}
            </Table.Cell>
            <Table.Cell>
              {row.to_date && <FormattedDate date={row.to_date} />}
            </Table.Cell>
            <Table.Cell>
              {row.repeat_every_year && <Icon name={checkSVG} />}
            </Table.Cell>
            <Table.Cell>{getDay(row.day)}</Table.Cell>
            <Table.Cell>{row.from_time}</Table.Cell>
            <Table.Cell>{row.to_time}</Table.Cell>
            {hasOrderTo && <Table.Cell>{row.order_to}</Table.Cell>}
          </Table.Row>
        ))}
      </Table.Body>
    </Table>
  );
};
TimetableClassicWidget.propTypes = {
  /**
   * Timetable
   */
  value: PropTypes.arrayOf(
    PropTypes.shape({
      from_date: PropTypes.instanceOf(Date),
      to_date: PropTypes.instanceOf(Date),
      repeat_every_year: PropTypes.bool,
      day: PropTypes.oneOf([
        'monday',
        'tuesday',
        'wednesday',
        'thursday',
        'friday',
        'saturday',
        'sunday',
      ]),
      from_time: PropTypes.string.isRequired,
      to_time: PropTypes.string.isRequired,
    }),
  ),
};

export default TimetableClassicWidget;
